// src/components/WorkspaceEmailSenders.tsx

// What? Workspace-level email sender address management
// Why? To let workspace admins configure which sender addresses are available
// How? List of sender addresses with add/delete functionality

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Sheet,
  Table,
  CircularProgress,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuthFetch } from "../utils/useAuthFetch";

interface WorkspaceEmailSendersProps {
  workspaceId: string;
}

interface SenderAddress {
  address: string;
  label: string;
}

const WorkspaceEmailSenders: React.FC<WorkspaceEmailSendersProps> = ({
  workspaceId,
}) => {
  const authFetch = useAuthFetch();
  const [senders, setSenders] = useState<SenderAddress[]>([]);
  const [newAddress, setNewAddress] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const loadSenders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authFetch(
        `/api/email/workspace/${workspaceId}/senders`,
        "fetch workspace senders",
      );
      setSenders(data?.sender_addresses || []);
    } catch {
      // Not configured
    } finally {
      setLoading(false);
    }
  }, [authFetch, workspaceId]);

  useEffect(() => {
    loadSenders();
  }, [loadSenders]);

  const saveSenders = async (updated: SenderAddress[]) => {
    setSaving(true);
    setFeedback(null);
    try {
      await authFetch(
        `/api/email/workspace/${workspaceId}/senders`,
        "save workspace senders",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sender_addresses: updated }),
        },
      );
      setSenders(updated);
      setFeedback({ type: "success", message: "Sender addresses updated" });
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to save: ${err}` });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleAdd = () => {
    if (!newAddress.trim()) return;
    const updated = [
      ...senders,
      {
        address: newAddress.trim(),
        label: newLabel.trim() || newAddress.trim(),
      },
    ];
    saveSenders(updated);
    setNewAddress("");
    setNewLabel("");
  };

  const handleDelete = (index: number) => {
    const updated = senders.filter((_, i) => i !== index);
    saveSenders(updated);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Sender list */}
      {senders.length > 0 && (
        <Sheet
          variant="outlined"
          sx={{ borderRadius: "sm", overflow: "hidden" }}
        >
          <Table size="sm">
            <thead>
              <tr>
                <th>Address</th>
                <th>Label</th>
                <th style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {senders.map((s, i) => (
                <tr key={i}>
                  <td>
                    <Typography level="body-sm">{s.address}</Typography>
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      sx={{ color: "text.secondary" }}
                    >
                      {s.label}
                    </Typography>
                  </td>
                  <td>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => handleDelete(i)}
                      disabled={saving}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {senders.length === 0 && (
        <Typography
          level="body-sm"
          sx={{ color: "text.tertiary", textAlign: "center", py: 2 }}
        >
          No sender addresses configured. Add one below.
        </Typography>
      )}

      {/* Add sender form */}
      <Stack direction="row" spacing={1.5} alignItems="flex-end">
        <FormControl sx={{ flex: 2 }}>
          <FormLabel>Email Address</FormLabel>
          <Input
            size="sm"
            type="email"
            placeholder="hr@company.com"
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Label</FormLabel>
          <Input
            size="sm"
            placeholder="HR"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
        </FormControl>
        <Button
          size="sm"
          startDecorator={<AddIcon />}
          onClick={handleAdd}
          disabled={!newAddress.trim() || saving}
          loading={saving}
        >
          Add
        </Button>
      </Stack>

      {feedback && (
        <Alert color={feedback.type} size="sm">
          {feedback.message}
        </Alert>
      )}
    </Stack>
  );
};

export default WorkspaceEmailSenders;
